<template>
  <div
    class="reel-clip-player-outer"
    :class="{ '-inactive': !isCurrentClip, '-loading': !isReady }"
  >
    <div
      ref="container"
      class="reel-clip-player-lining"
      @click="$emit('click')"
    >
      <!-- video goes here -->
    </div>

    <div
      v-if="!isReady"
      class="loading-overlay"
    >
      <Spinner
        v-if="hasEverPlayed"
        class="video-spinner"
      />
    </div>
  </div>
</template>

<script>
import { ClipBuffer } from '@/services/clip-buffer'
import { mapActions, mapState } from '@/utils/namespaced-mappings'
import Spinner from '@/components/Spinner'
import timer from '@/utils/timer'

export default {
    components: { Spinner },

    props: {
        namespace: { type: String, required: true },
        clipId: { required: true },
    },

    data() {
        const clip = this.$store.getters[this.namespace + '/getClipById'](this.clipId)

        return {
            clip,
            buffer: null,
            shownTime: 0,

            timeUpdateHandler: null,
            finishHandler: null,
            errorHandler: null,

            isReallyInBounds: false,
            inBoundsTimer: timer(() => {
                this.isReallyInBounds = true
            }, 1000),
        }
    },

    computed: {
        ...mapState([
            'playingClipId',
            'isPaused',
            'isMuted',
            'volume',
            'isFullScreen',
            'relativeTimeSetting',
            'hasEverPlayed',
        ]),

        seemsInBounds() {
            return this.buffer && this.buffer.isInBounds
        },

        isCurrentClip() {
            return this.clipId == this.playingClipId
        },

        isReady() {
            return this.isReallyInBounds && this.buffer.canPlay
        },
    },

    mounted() {
        this.loadVideo()
        this.applyEvents()
        this.onClipTransition()
    },

    beforeDestroy() {
        this.removeEvents()
        this.buffer.destroy()
    },

    methods: {
        ...mapActions([
            'finishClip',
            'trackRelativeTime',
            'pause',
        ]),

        loadVideo() {
            this.buffer = new ClipBuffer(this.clip, this.$refs.container)
        },

        applyEvents() {
            this.buffer.on('timeUpdate', this.timeUpdateHandler = time => {
                if (this.isCurrentClip) {
                    const milliseconds = time * 1000
                    const startTime = (this.clip ? this.clip.start_time : 0)
                    const relativeTime = milliseconds - startTime

                    this.trackRelativeTime({ clipId: this.clipId, relativeTime })
                }
            })

            this.buffer.on('finish', this.finishHandler = () => {
                if (this.isCurrentClip) {
                    this.finishClip()
                }
            })

            this.buffer.on('error', this.errorHandler = () => {
                if (this.isCurrentClip) {
                    this.pause()
                }
            })
        },

        removeEvents() {
            if (this.buffer) {
                this.buffer.off('timeUpdate', this.timeUpdateHandler)
                this.buffer.off('finish', this.finishHandler)
                this.buffer.off('error', this.errorHandler)
            }
        },

        scrubToRequestedTime() {
            const time = this.relativeTimeSetting.time / 1000
            this.buffer.scrubToRelativeTime(time)
        },

        // Called when isCurrentClip changes
        // or when the component is first mounted
        onClipTransition() {
            if (this.isCurrentClip) {
                this.isReallyInBounds = this.seemsInBounds

                this.scrubToRequestedTime()

                if (this.isReady && !this.isPaused) {
                    this.buffer.resume()
                }
            } else {
                this.buffer.pause()
            }

            this.updateBufferVolume()
        },

        updateBufferVolume() {
            this.buffer.setVolume(this.volume)

            if (this.isCurrentClip) {
                this.buffer.setMuted(this.isMuted)
            } else {
                this.buffer.setMuted(true)
            }
        },
    },

    watch: {
        relativeTimeSetting() {
            this.scrubToRequestedTime()
        },

        seemsInBounds(seemsInBounds) {
            if (seemsInBounds) {
                // The player seems to be in bounds and buffered,
                // but might suddenly act up, so use the timer to wait a moment to be sure.
                this.inBoundsTimer.start()
            } else {
                this.isReallyInBounds = false
                this.inBoundsTimer.stop()
            }
        },

        isCurrentClip() {
            this.onClipTransition()
        },

        isReady(isReady) {
            if (!isReady) {
                this.buffer.pause()
            } else if (this.isCurrentClip && !this.isPaused) {
                this.buffer.resume()
            }
        },

        isPaused() {
            if (this.isPaused) {
                this.buffer.pause()
            } else if (this.isCurrentClip && this.isReady) {
                this.buffer.resume()
            }
        },

        isMuted() {
            this.updateBufferVolume()
        },

        volume() {
            this.updateBufferVolume()
        },
    },
}
</script>

<style lang="scss" scoped>
    .-inactive {
        opacity: 0 !important;
        z-index: -100 !important;
    }

    .-loading .reel-clip-player-lining {
        opacity: 0 !important;
    }

    .reel-clip-player-outer {
        background: black;
    }

    .reel-clip-player-outer,
    .reel-clip-player-lining,
    .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }

    .loading-overlay {
        background: black;
    }

    .reel-clip-player.-loading .reel-clip-player-lining {
        display: none !important;
    }

    .video-spinner {
        position: absolute;
        top: calc(50% - 23px);
        left: calc(50% - 23px);
        transform: translate(-50%, -50%);
    }
</style>

<style lang="scss">
    .reel-clip-player {
        .video-js {
            position: relative;
            width: 100% !important;
            height: 100% !important;

            cursor: pointer;

            display: flex;
            align-items: center;
            justify-content: center;
        }

        .vjs-loading-spinner {
            animation: hide-initially 1s 1;
        }

        .vjs-volume-panel,
        .vjs-progress-control,
        .vjs-time-control,
        .vjs-live-control,
        .vjs-seek-to-live-control,
        .vjs-fullscreen-control,
        .vjs-play-control,
        .vjs-control-bar,
        .vjs-big-play-button,
        .vjs-resize-manager
        {
            display: none !important;
        }

        .vjs-tech {
            position: relative;
            pointer-events: none !important;
        }
    }

    @keyframes hide-initially {
        0%, 99% { opacity: 0 }
        100% { opacity: 1 }
    }
</style>